import * as React from "react"


import { cn } from "@/utils"

// Every component here accepts both `className` and `class` (merged, `class` last). Under
// preact/compat a vnode carrying both attributes drops `class`, so a caller writing
// `<CardTitle class="flex …">` would silently lose its classes if we only merged `className` and
// spread the rest — the same trap as the lucide-react icons (see AGENTS.md).
type WithClass<T> = T & { class?: string }

const Card = React.forwardRef<
  HTMLDivElement,
  WithClass<React.HTMLAttributes<HTMLDivElement>>
>(({ className, class: classAttr, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border bg-card text-card-foreground shadow-xs",
      className,
      classAttr
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  WithClass<React.HTMLAttributes<HTMLDivElement>>
>(({ className, class: classAttr, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className, classAttr)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  WithClass<React.HTMLAttributes<HTMLHeadingElement>>
>(({ className, class: classAttr, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className,
      classAttr
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  WithClass<React.HTMLAttributes<HTMLParagraphElement>>
>(({ className, class: classAttr, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className, classAttr)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  WithClass<React.HTMLAttributes<HTMLDivElement>>
>(({ className, class: classAttr, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className, classAttr)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  WithClass<React.HTMLAttributes<HTMLDivElement>>
>(({ className, class: classAttr, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className, classAttr)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
